import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Label,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";

import LoadingSpinner from "../../../../Components/Common/Spinner";
import AppModal from "../../../../Components/Common/AppModal";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";

// Service layer — the server scopes everything to the logged-in user's OWN
// company (Company_Id from the token), so the front end never sends a company id.
const listPeriods = () => FetchAPI("/companyvatregistration/list", "GET");
const createPeriod = (rows) =>
  FetchAPI("/companyvatregistration/create", "POST", rows);
const updatePeriod = (rows) =>
  FetchAPI("/companyvatregistration/update", "PUT", rows);
const deregisterPeriod = (body) =>
  FetchAPI("/companyvatregistration/deregister", "POST", body);
const deletePeriod = (ids) =>
  FetchAPI("/companyvatregistration/delete", "DELETE", ids);

const today = () => new Date().toISOString().slice(0, 10);
const dateOnly = (v) => (v ? String(v).slice(0, 10) : "");
const fmt = (v) => {
  const d = dateOnly(v);
  if (!d) return "";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};

const emptyForm = {
  Id: null,
  StartDate: today(),
  EndDate: "", // blank = open / still registered
  VatNo: "",
  Notes: "",
};

// Settings > Registration & VAT > VAT registration history.
// The company's own VAT registration is a dated history: one row per period it
// was VAT-registered (StartDate .. EndDate; a blank EndDate means "still
// registered"). This is what every document uses to decide, BY ITS OWN DATE,
// whether VAT is charged/shown — so a company can move No VAT -> registered ->
// deregistered -> re-registered and old documents stay correct.
export default function VatRegistrationHistory() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  // Deregister modal
  const [deregOpen, setDeregOpen] = useState(false);
  const [deregDate, setDeregDate] = useState(today());
  const [deregRow, setDeregRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listPeriods();
      if (res?.status === 200) {
        setPeriods(res.companyvatregistration_list || []);
      } else {
        toast.error(res?.message || "Failed to load VAT registration history");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Is the company VAT-registered today? True when a period covers today.
  const currentStatus = useMemo(() => {
    const t = today();
    const active = periods.find((p) => {
      const s = dateOnly(p.StartDate);
      const e = dateOnly(p.EndDate);
      return s && s <= t && (!e || e >= t);
    });
    return active || null;
  }, [periods]);

  const hasOpenPeriod = useMemo(
    () => periods.some((p) => !dateOnly(p.EndDate)),
    [periods]
  );

  const openRegister = () => {
    setForm({ ...emptyForm, StartDate: today() });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      Id: row.Id,
      StartDate: dateOnly(row.StartDate),
      EndDate: dateOnly(row.EndDate),
      VatNo: row.VatNo || "",
      Notes: row.Notes || "",
    });
    setModalOpen(true);
  };

  const validateForm = () => {
    if (!form.StartDate) {
      toast.error("Enter a start date");
      return false;
    }
    if (form.EndDate && form.EndDate < form.StartDate) {
      toast.error("End date cannot be before the start date");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const row = {
        StartDate: form.StartDate,
        EndDate: form.EndDate || null,
        VatNo: form.VatNo ? form.VatNo.trim() : null,
        Notes: form.Notes ? form.Notes.trim() : null,
      };
      if (form.Id) row.Id = form.Id;
      const res = form.Id ? await updatePeriod([row]) : await createPeriod([row]);
      if (res?.status === 200) {
        toast.success(form.Id ? "Period updated" : "Registration added");
        setModalOpen(false);
        load();
      } else {
        toast.error(res?.message || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const openDeregister = (row) => {
    setDeregRow(row);
    setDeregDate(today());
    setDeregOpen(true);
  };

  const handleDeregister = async () => {
    if (deregRow && deregDate < dateOnly(deregRow.StartDate)) {
      toast.error("Deregistration date cannot be before the start date");
      return;
    }
    setSaving(true);
    try {
      const res = await deregisterPeriod({ EndDate: deregDate });
      if (res?.status === 200) {
        toast.success("Deregistered");
        setDeregOpen(false);
        load();
      } else {
        toast.error(res?.message || "Deregister failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remove this VAT registration period?")) return;
    const res = await deletePeriod([row.Id]);
    if (res?.status === 200) {
      toast.success("Period removed");
      load();
    } else {
      toast.error(res?.message || "Remove failed");
    }
  };

  return (
    <Card>
      <CardHeader className="d-flex align-items-center">
        <div>
          <span className="fs-14 fw-semibold">VAT Registration History</span>
          <div className="text-muted small">
            Every invoice, quote and statement decides whether to charge and
            show VAT from this history, based on the document&apos;s own date.
          </div>
        </div>
        <div className="ms-auto">
          <Button color="primary" size="sm" onClick={openRegister}>
            <i className="ri-add-line align-bottom me-1" />
            Add registration
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        <div className="mb-3">
          {currentStatus ? (
            <Badge color="success" className="fs-12">
              <i className="ri-checkbox-circle-line align-bottom me-1" />
              VAT registered today
              {currentStatus.VatNo ? ` — ${currentStatus.VatNo}` : ""}
            </Badge>
          ) : (
            <Badge color="secondary" className="fs-12">
              <i className="ri-close-circle-line align-bottom me-1" />
              Not VAT registered today
            </Badge>
          )}
        </div>

        {loading && periods.length === 0 ? (
          <LoadingSpinner />
        ) : (
          <div className="table-responsive">
            <Table className="align-middle table-nowrap mb-0">
              <thead className="text-muted">
                <tr>
                  <th>Period</th>
                  <th>VAT number</th>
                  <th>Notes</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {periods.length ? (
                  periods.map((row) => {
                    const open = !dateOnly(row.EndDate);
                    return (
                      <tr key={row.Id}>
                        <td>
                          <span className="fw-semibold">
                            {fmt(row.StartDate)}
                          </span>{" "}
                          &ndash;{" "}
                          {open ? (
                            <Badge color="success-subtle" className="text-success">
                              Current
                            </Badge>
                          ) : (
                            <span className="fw-semibold">{fmt(row.EndDate)}</span>
                          )}
                        </td>
                        <td>{row.VatNo || <span className="text-muted">-</span>}</td>
                        <td className="text-truncate" style={{ maxWidth: 220 }}>
                          {row.Notes || <span className="text-muted">-</span>}
                        </td>
                        <td className="text-end">
                          {open && (
                            <Button
                              size="sm"
                              color="light"
                              className="me-1"
                              title="Deregister (close this period)"
                              onClick={() => openDeregister(row)}
                            >
                              <i className="ri-logout-box-r-line" /> Deregister
                            </Button>
                          )}
                          <Button
                            size="sm"
                            color="light"
                            className="me-1"
                            onClick={() => openEdit(row)}
                          >
                            <i className="ri-pencil-line" />
                          </Button>
                          <Button
                            size="sm"
                            color="light"
                            onClick={() => handleDelete(row)}
                          >
                            <i className="ri-delete-bin-line text-danger" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      No VAT registration recorded. If you are VAT registered,
                      add a period with the date you registered.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>

      <AppModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={form.Id ? "Edit VAT registration period" : "Add VAT registration"}
        size="sm"
        loading={saving}
        onSubmit={handleSave}
        submitLabel={form.Id ? "Save" : "Add"}
      >
        <FormGroup>
          <Label>Registered from</Label>
          <Input
            type="date"
            value={form.StartDate}
            onChange={(e) =>
              setForm((f) => ({ ...f, StartDate: e.target.value }))
            }
          />
        </FormGroup>
        <FormGroup>
          <Label>Registered to</Label>
          <Input
            type="date"
            value={form.EndDate}
            onChange={(e) => setForm((f) => ({ ...f, EndDate: e.target.value }))}
          />
          <small className="text-muted">
            Leave blank if still registered (open period).
          </small>
        </FormGroup>
        <FormGroup>
          <Label>VAT number</Label>
          <Input
            type="text"
            placeholder="e.g. GB123456789"
            value={form.VatNo}
            onChange={(e) => setForm((f) => ({ ...f, VatNo: e.target.value }))}
          />
        </FormGroup>
        <FormGroup>
          <Label>Notes</Label>
          <Input
            type="textarea"
            rows={2}
            value={form.Notes}
            onChange={(e) => setForm((f) => ({ ...f, Notes: e.target.value }))}
          />
        </FormGroup>
      </AppModal>

      <AppModal
        open={deregOpen}
        onClose={() => setDeregOpen(false)}
        title="Deregister for VAT"
        size="sm"
        loading={saving}
        onSubmit={handleDeregister}
        submitLabel="Deregister"
      >
        <FormGroup>
          <Label>Deregistration date</Label>
          <Input
            type="date"
            value={deregDate}
            onChange={(e) => setDeregDate(e.target.value)}
          />
          <small className="text-muted">
            Documents dated after this will no longer charge or show VAT.
          </small>
        </FormGroup>
      </AppModal>
    </Card>
  );
}
